import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { formatNumber } from "../../utils/format.ts";
import { api, getErrorMessage, type BulkAddJob } from "../../api/client.ts";
import { keys } from "../../api/queryKeys.ts";
import { useOrganization } from "../../contexts/OrganizationContext.tsx";
import { useTenant } from "../../contexts/TenantContext.tsx";
import FilterBar from "../../components/filters/FilterBar.tsx";
import SavedViewsMenu from "../../components/filters/SavedViewsMenu.tsx";
import { useFilters } from "../../components/filters/useFilters.ts";
import ListSelector from "../../components/ListSelector.tsx";
import PaginationControls from "../../components/PaginationControls.tsx";
import { useCommaParam, useUrlSort, useUrlState } from "../../hooks/useUrlState.ts";
import BulkSelectionBar from "./components/BulkSelectionBar.tsx";
import CompaniesTable from "./components/CompaniesTable.tsx";
import ExportButton from "./components/ExportButton.tsx";
import { useCompanyFilterSpecs } from "./companyFilters.ts";
import { parseIntOrUndefined } from "./utils.ts";
import PageHeader from "../../components/PageHeader.tsx";

export default function CompaniesPage() {
  const navigate = useNavigate();
  const { tenantPath, selectedTenant, isLoading: tenantLoading } = useTenant();
  const { currentOrg } = useOrganization();
  // Money filters and columns are in the tenant's currency.
  const currency = selectedTenant?.currency ?? "NOK";
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [selectAllMatching, setSelectAllMatching] = useState(false);
  const [listSelectorOpen, setListSelectorOpen] = useState(false);

  const addToList = useMutation<
    { added: number } | BulkAddJob,
    Error,
    { listId: string; listName: string }
  >({
    mutationFn: ({ listId }) => {
      if (selectAllMatching) {
        return api.bulkAddProspectsFromFilter(listId, queryParams);
      }
      return api.addProspectsToList(listId, Array.from(selected));
    },
    onSuccess: (data, { listName }) => {
      queryClient.invalidateQueries({ queryKey: keys.prospectList.all });
      if ("added" in data) {
        toast.success(`Added ${data.added} companies to "${listName}"`);
      } else {
        const n = data.matched_count ?? data.added_count ?? 0;
        toast.success(`Bulk add started for ~${formatNumber(n)} companies. Running in background.`);
      }
      setSelected(new Set());
      setSelectAllMatching(false);
    },
  });

  // Keyset pagination — stateless cursor in URL, stack in memory so Prev can
  // unwind. Every filter, sort and page-size change drops the cursor and the
  // stack (and select-all) so keyset state stays consistent. Refreshing keeps
  // the current page (cursor in URL) but disables Prev.
  const [cursorStack, setCursorStack] = useState<(string | null)[]>([]);
  const url = useUrlState({
    resetKey: "cursor",
    onFilterChange: () => {
      setCursorStack([]);
      setSelectAllMatching(false);
    },
  });
  const { searchParams, updateParams, changeFilter } = url;

  const { specs, contactRoleOptions } = useCompanyFilterSpecs(currency);
  const filters = useFilters(specs, {
    state: url,
    search: { param: "name", placeholder: "Search companies" },
    pageKey: "companies",
    scopeId: selectedTenant?.id,
    remember: true,
  });

  const aiIndustries = useCommaParam(searchParams, "industry_keywords");
  const servedVerticals = useCommaParam(searchParams, "served_vertical_keywords");
  const technologies = useCommaParam(searchParams, "tech");
  const countries = useCommaParam(searchParams, "country");
  const orgForms = useCommaParam(searchParams, "org_form");
  const employeeBuckets = useCommaParam(searchParams, "emp_bucket");
  const jobAdKeywords = useCommaParam(searchParams, "job_kw");
  const contactRoleTypes = useCommaParam(searchParams, "contact_role");
  const param = (key: string) => searchParams.get(key) ?? "";
  const regulatoryFilingStatus = param("reg_status") as "" | "active" | "completed";

  const { sortBy, sortDir, handleSort } = useUrlSort(url, "name", "asc");
  const cursor = searchParams.get("cursor");
  const rowsPerPage = parseInt(searchParams.get("per_page") ?? "50", 10) || 50;

  // Options in the ChipAutocomplete shape the export dialog takes.
  const contactRoleTypeOptions = useMemo(
    () => contactRoleOptions.map((o) => ({ code: o.value, label: o.label })),
    [contactRoleOptions],
  );

  const queryParams = {
    name: param("name") || undefined,
    country: countries.length ? countries : undefined,
    industry_keywords: aiIndustries.length ? aiIndustries : undefined,
    served_vertical_keywords: servedVerticals.length ? servedVerticals : undefined,
    org_form: orgForms.length ? orgForms : undefined,
    employee_size_bucket: employeeBuckets.length ? employeeBuckets : undefined,
    job_ad_keywords: jobAdKeywords.length ? jobAdKeywords : undefined,
    technology: technologies.length ? technologies : undefined,
    contact_role_type: contactRoleTypes.length ? contactRoleTypes : undefined,
    contact_name: param("contact_name") || undefined,
    revenue_min: parseIntOrUndefined(param("rev_min")),
    revenue_max: parseIntOrUndefined(param("rev_max")),
    operating_result_min: parseIntOrUndefined(param("opr_min")),
    operating_result_max: parseIntOrUndefined(param("opr_max")),
    currency,
    regulatory_filing_keyword: param("reg_kw") || undefined,
    regulatory_filing_status: regulatoryFilingStatus || undefined,
    sort_by: sortBy,
    sort_dir: sortDir,
    limit: rowsPerPage,
    cursor: cursor ?? undefined,
  };

  const { data, isLoading, isError } = useQuery({
    queryKey: keys.masterCompany.list(queryParams),
    queryFn: () => api.getMasterCompanies(queryParams),
    // Wait for the tenant: its currency is the unit of the money filters.
    enabled: !tenantLoading,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });

  const items = data?.items ?? [];

  return (
    <>
      <PageHeader
        title="Companies"
        description={
          <>
            Find companies to prospect{selectedTenant ? ` for ${selectedTenant.name}` : ""}
          </>
        }
        actions={
          data &&
          data.total > 0 && (
            <ExportButton
              total={data.total}
              contactRoleTypeOptions={contactRoleTypeOptions}
              onExport={async (fields, roleColumns) => {
                const { limit: _, cursor: __, ...exportParams } = queryParams;
                try {
                  await api.exportMasterCompanies(exportParams, fields, roleColumns);
                } catch (err) {
                  toast.error(getErrorMessage(err));
                  throw err;
                }
              }}
            />
          )
        }
      />
      <BulkSelectionBar
        selectedCount={selected.size}
        addToListPending={addToList.isPending}
        onAddToListClick={() => setListSelectorOpen(true)}
        onClear={() => {
          setSelected(new Set());
          setSelectAllMatching(false);
        }}
        selectAllMatching={selectAllMatching}
        totalMatching={data?.total}
      />
      {data &&
        !selectAllMatching &&
        items.length > 0 &&
        selected.size === items.length &&
        data.total > items.length && (
          <Alert
            severity="info"
            sx={{ mb: 2, alignItems: "center" }}
            action={
              <Button color="inherit" size="small" onClick={() => setSelectAllMatching(true)}>
                Select all {formatNumber(data.total)} matching filter
              </Button>
            }
          >
            All {items.length} on this page selected.
          </Alert>
        )}
      <CompaniesTable
        items={items}
        loading={isLoading || tenantLoading}
        filtered={filters.activeCount > 0}
        toolbar={
          <FilterBar
            filters={filters}
            count={data ? `${formatNumber(data.total)} ${data.total === 1 ? "company" : "companies"}` : undefined}
            views={
              <SavedViewsMenu
                filters={filters}
                context={{
                  tenantId: selectedTenant?.id,
                  tenantName: selectedTenant?.name,
                  organizationId: currentOrg?.id,
                  organizationName: currentOrg?.name,
                }}
              />
            }
          />
        }
        failed={isError}
        selected={selected}
        onToggleAll={(checked) => {
          setSelected(checked ? new Set(items.map((c) => c.id)) : new Set());
          if (!checked) setSelectAllMatching(false);
        }}
        onToggleRow={(id, checked) => {
          setSelectAllMatching(false);
          setSelected((prev) => {
            const next = new Set(prev);
            if (checked) next.add(id);
            else next.delete(id);
            return next;
          });
        }}
        sortBy={sortBy}
        sortDir={sortDir}
        onSort={handleSort}
        onRowClick={(id) => navigate(tenantPath(`/companies/${id}`))}
        currency={data?.currency ?? currency}
        footer={
          data &&
          items.length > 0 && (
            <PaginationControls
              rowsPerPage={rowsPerPage}
              pageItemCount={items.length}
              total={data.total}
              cursorStackDepth={cursorStack.length}
              hasPrev={cursorStack.length > 0}
              hasNext={!!data.next_cursor}
              onChangeRowsPerPage={(n) =>
                changeFilter({ per_page: n === 50 ? null : String(n) })
              }
              onPrev={() => {
                const prev = cursorStack[cursorStack.length - 1];
                setCursorStack((stack) => stack.slice(0, -1));
                updateParams({ cursor: prev ?? null });
              }}
              onNext={() => {
                if (!data.next_cursor) return;
                setCursorStack((stack) => [...stack, cursor]);
                updateParams({ cursor: data.next_cursor });
              }}
            />
          )
        }
      />
      <ListSelector
        open={listSelectorOpen}
        onClose={() => setListSelectorOpen(false)}
        onSelect={(listId, listName) => {
          setListSelectorOpen(false);
          addToList.mutate({ listId, listName });
        }}
      />
    </>
  );
}
